import { ReactNode, useEffect, useMemo } from "react";
import { UserInfoDto } from "@semo/api-contracts";
import { VendorAuthContext } from "./VendorAuthContext";
import { useEmbedAuth } from "../hooks/useEmbedAuth";
import {
  EmbedLoadingState,
  EmbedErrorState,
  EmbedUnauthorizedState,
} from "../../presentation/components/EmbedAuthStates";

interface EmbedAuthProviderProps {
  children: ReactNode;
}

/**
 * EmbedAuthProvider
 *
 * Provides VendorAuthContext for embed pages using token-based authentication.
 * Handles the `useEmbedAuth` verification flow and exposes the result via context.
 *
 * This replaces the need for individual pages to handle auth states *if* they rely on components
 * that require VendorAuthContext (like VendorChatWindow).
 */
export function EmbedAuthProvider({ children }: EmbedAuthProviderProps) {
  const {
    isAuthenticated,
    isVerifying,
    error,
    retry,
    authPayload,
    supplierInfo,
  } = useEmbedAuth();

  // Convert Embed payload to UserInfoDto-like structure for the context
  const currentUser: UserInfoDto | null = useMemo(() => {
    if (!isAuthenticated || !authPayload?.userId) return null;

    return {
      id: authPayload.userId,
      contactEmail: supplierInfo?.contactEmail || "",
      name: supplierInfo?.managerName || supplierInfo?.companyName || "공급사",
      userType: "supplier",
    } as UserInfoDto;
  }, [isAuthenticated, authPayload, supplierInfo]);

  // Context value
  const contextValue = {
    currentUser,
    supplierInfo: supplierInfo ?? null,
    isLoading: isVerifying,
    isSessionLoading: isVerifying,
    // Embed users don't have a traditional "logout" or "checkSession"
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    logout: async () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setCurrentUser: () => {},
    checkSession: () => retry(), // Retry verification on checkSession
  };

  // Render Auth States directly here (Layout Wrapper behavior)
  if (isVerifying) {
    return <EmbedLoadingState message="인증 확인 중..." />;
  }

  if (error) {
    return <EmbedErrorState message={error} onRetry={retry} />;
  }

  if (!isAuthenticated) {
    return <EmbedUnauthorizedState />;
  }

  return (
    <VendorAuthContext.Provider value={contextValue}>
      {children}
    </VendorAuthContext.Provider>
  );
}
